import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Truck, AlertTriangle, ListChecks, Fuel, Building2, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { fmtKg } from '@/lib/missions';
import { gnf } from '@/lib/invoices';
import { QueryError } from '@/components/QueryError';
import { useHasPermission } from '@/lib/permissions';

import type { DashboardKpis } from '@logistech/ui-shared';

/** GET /dashboard/clients-activity (missions-service, permission dashboard.read_activity) */
export interface ClientsActivity {
  period: '7d' | '30d' | '90d' | 'all';
  clients: {
    clientId: string;
    clientName: string;
    missionCount: number;
    totalWeightKg: number;
    /** null si aucune facture émise en GNF sur la période */
    revenueGnf: number | null;
  }[];
  /** Clients distincts avec au moins une mission (hors brouillon / annulée) sur la période */
  totalClients: number;
}

export const Route = createFileRoute('/_authed/dashboard')({ component: DashboardPage });

export function DashboardPage() {
  const kpisQuery = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => (await api.get<DashboardKpis>('/dashboard/kpis')).data,
    refetchInterval: 30_000,
  });
  const { data: kpis } = kpisQuery;

  // Activité par client agrégée côté serveur (BUG-017) ; sans la permission, aucun appel voué au 403
  const canReadActivity = useHasPermission('dashboard.read_activity');
  const canReadMissions = useHasPermission('missions.read');
  const activityQuery = useQuery({
    queryKey: ['dashboard', 'clients-activity', { period: '30d', limit: 5 }],
    queryFn: async () => (await api.get<ClientsActivity>('/dashboard/clients-activity?period=30d&limit=5')).data,
    refetchInterval: 30_000,
    enabled: canReadActivity,
  });
  const { data: activity } = activityQuery;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Tableau de bord</h1>
      <QueryError query={[kpisQuery, activityQuery]} />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard
          label="Véhicules en ligne"
          value={`${kpis?.vehiclesOnline ?? 0} / ${kpis?.vehiclesTotal ?? 0}`}
          icon={Truck}
          accent="text-primary-500"
        />
        <KpiCard
          label="Missions en cours"
          value={String(kpis?.missionsInProgress ?? 0)}
          icon={ListChecks}
          accent="text-accent-500"
        />
        <KpiCard
          label="Alertes ouvertes"
          value={String(kpis?.alertsOpen ?? 0)}
          sub={`${kpis?.alertsCritical ?? 0} critiques`}
          icon={AlertTriangle}
          accent="text-danger"
        />
        {canReadActivity && (
          <KpiCard
            label="Clients actifs"
            value={String(activity?.totalClients ?? 0)}
            sub="avec missions sur 30 jours"
            icon={Building2}
            accent="text-primary-500"
          />
        )}
        <KpiCard label="Conso 7j" value="—" icon={Fuel} accent="text-success" />
      </div>

      {canReadActivity && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Top clients (30 jours)</CardTitle>
            {canReadMissions && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild><Link to="/clients">Voir tous les clients</Link></Button>
                <Button size="sm" asChild>
                  <Link to="/clients/new"><Plus className="h-4 w-4 mr-1" /> Ajouter un client</Link>
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Missions</TableHead>
                  <TableHead className="text-right">Tonnage</TableHead>
                  <TableHead className="text-right">CA facturé</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity?.clients.map((c) => (
                  <TableRow key={c.clientId}>
                    <TableCell className="font-semibold">
                      {canReadMissions
                        ? <Link to="/clients/$id" params={{ id: c.clientId }} className="hover:underline">{c.clientName}</Link>
                        : c.clientName}
                    </TableCell>
                    <TableCell className="text-right">{c.missionCount}</TableCell>
                    <TableCell className="text-right">{fmtKg(c.totalWeightKg)}</TableCell>
                    <TableCell className="text-right">{c.revenueGnf === null ? '—' : gnf(c.revenueGnf)}</TableCell>
                  </TableRow>
                ))}
                {activity && activity.clients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Aucune mission client sur les 30 derniers jours.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Missions sur 7 jours</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={[]}>
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0B3D91" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top 5 véhicules consommateurs</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={[]}>
                <XAxis dataKey="plate" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="liters" fill="#F58220" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
}

const KpiCard = ({ label, value, sub, icon: Icon, accent }: KpiCardProps) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-4">
      <Icon className={`h-10 w-10 ${accent}`} />
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);
